import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import update from 'immutability-helper';
import mutate from '../../../../shared/mutate';
import Validator from '../../../../shared/validator';
import defaultOnErrorHandler from '../../../../shared/default-on-error-handler';
import Loading from '../../../../shared/loading';
import { EMAIL_REGEX } from '../../../../shared/consts';
import SuccessInfo from './success-info';
import Footer from './footer';
import { PDS_LABEL, REPORTS_LABEL, OTHER_LABEL } from './consts';
import sendContact from '../../../../services/partnerships/contact-service';

export default class Form extends React.Component {
  static propTypes = {
    wireId: PropTypes.number.isRequired,
    partnershipId: PropTypes.number,
    investorTypes: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    ).isRequired,
    organization: PropTypes.string.isRequired,
  };

  static defaultProps = {
    partnershipId: null,
  };

  constructor(props) {
    super(props);

    this.validator = new Validator({
      nameRequired: state => !!state.name,
      emailRequired: state => !!state.email,
      emailRegex: state => !state.email || EMAIL_REGEX.test(state.email),
    });

    const { currentUser } = window.Livewire;
    this.state = {
      name: _.get(currentUser, 'profile.name', ''),
      email: _.get(currentUser, 'email', ''),
      phone: '',
      investorType: _.get(currentUser, 'profile.investorType.name', _.get(props, 'investorTypes[0].name')),
      pds: false,
      reports: false,
      other: false,
      message: '',
      submitInProgress: false,
      validations: this.validator.getValidations(),
    };
  }

  handlePdsChange(event) {
    this.setState({ pds: event.target.checked });
  }

  handleReportsChange(event) {
    this.setState({ reports: event.target.checked });
  }

  handleOtherChange(event) {
    this.setState({ other: event.target.checked });
  }

  handleEmailChange(event) {
    const email = event.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.email = email;
      state.validations.emailRequiredValid = this.validator.validateEmailRequired(state);
      state.validations.emailRegexValid = this.validator.validateEmailRegex(state);
    }));
  }

  handleNameChange(event) {
    const name = event.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.name = name;
      state.validations.nameRequiredValid = this.validator.validateNameRequired(
        state,
      );
    }));
  }

  handlePhoneChange(event) {
    this.setState({ phone: event.target.value });
  }

  handleInvestorTypeChange(event) {
    this.setState({ investorType: event.target.value });
  }

  handleMessageChange(event) {
    this.setState({ message: event.target.value });
  }

  handleSubmit(event) {
    event.preventDefault();

    this.setState(
      state => update(state, {
        validations: { $set: this.validator.validateForm(state) },
      }),
      () => {
        if (!this.validator.formValid) {
          return;
        }
        this.loading.start();
        this.setState({
          submitInProgress: true,
        });

        sendContact(this.props.partnershipId, {
          referable_id: this.props.wireId,
          referable_type: 'Wire',
          name: this.state.name,
          email: this.state.email,
          phone: this.state.phone,
          investor_type: this.state.investorType,
          enquiring_about: [
            this.state.pds ? PDS_LABEL : '',
            this.state.reports ? REPORTS_LABEL : '',
            this.state.other ? OTHER_LABEL : '',
          ].filter(x => !!x),
          message_to_contributor: this.state.message,
        })
          .then(() => this.setState({ submitSuccessful: true }))
          .catch(e => defaultOnErrorHandler(e, 'Error submitting contact request'))
          .finally(() => {
            this.loading.stop();
            this.setState({ submitInProgress: false });
          });
      },
    );
  }

  render() {
    const nameFormGroupClass = classNames(
      'form-group',
      'lead-capture-form-group',
      '--first',
      {
        'has-error': !this.state.validations.nameRequiredValid,
      },
    );
    const emailFormGroupClass = classNames(
      'form-group',
      'lead-capture-form-group',
      {
        'has-error': !this.state.validations.emailRequiredValid || !this.state.validations.emailRegexValid,
      },
    );

    return (
      <form
        className="lead-capture-form --user-facing"
        onSubmit={e => this.handleSubmit(e)}
      >
        {this.state.submitSuccessful && <SuccessInfo organization={this.props.organization} />}
        <div className="lead-capture-form-body">
          <div className="lead-capture-form-column --left">
            <p className={nameFormGroupClass}>
              <label htmlFor="name" className="lead-capture-form-label">
                Name
                <input
                  name="name"
                  type="text"
                  value={this.state.name}
                  onChange={e => this.handleNameChange(e)}
                  disabled={this.state.submitInProgress}
                  readOnly={this.state.submitInProgress}
                />
              </label>
              {this.state.validations.nameRequiredValid || (
                <span className="help-block">Name is required</span>
              )}
            </p>

            <p className={emailFormGroupClass}>
              <label htmlFor="email" className="lead-capture-form-label">
                Email
                <input
                  name="email"
                  type="text"
                  value={this.state.email}
                  onChange={e => this.handleEmailChange(e)}
                  disabled={this.state.submitInProgress}
                  readOnly={this.state.submitInProgress}
                />
              </label>
              {this.state.validations.emailRequiredValid || (
                <span className="help-block">Email is required</span>
              )}
              {this.state.validations.emailRegexValid || (
                <span className="help-block">Email is not in correct format</span>
              )}
            </p>

            <p className="form-group lead-capture-form-group">
              <label htmlFor="phone" className="lead-capture-form-label">
                Phone
                <input
                  name="phone"
                  type="text"
                  value={this.state.phone}
                  onChange={e => this.handlePhoneChange(e)}
                  disabled={this.state.submitInProgress}
                  readOnly={this.state.submitInProgress}
                />
              </label>
            </p>

            <p className="form-group lead-capture-form-group">
              <label htmlFor="investorType" className="lead-capture-form-label">
                Investor Type
                <select
                  name="investorType"
                  type="text"
                  value={this.state.investorType}
                  onChange={e => this.handleInvestorTypeChange(e)}
                  disabled={this.state.submitInProgress}
                  readOnly={this.state.submitInProgress}
                >
                  {this.props.investorTypes.map(type => (
                    <option value={type.name} key={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </label>
            </p>
          </div>

          <div className="lead-capture-form-column">
            <h4>I would like to</h4>

            <p className="form-group lead-capture-form-group --checkbox-stack --first">
              <label htmlFor="pds" className="lead-capture-form-label --checkbox-label">
                <input
                  name="pds"
                  type="checkbox"
                  checked={this.state.pds}
                  onChange={e => this.handlePdsChange(e)}
                  disabled={this.state.submitInProgress}
                  readOnly={this.state.submitInProgress}
                />
                {' '}
                {PDS_LABEL}
              </label>
              <label htmlFor="report" className="lead-capture-form-label --checkbox-label">
                <input
                  name="report"
                  type="checkbox"
                  checked={this.state.reports}
                  onChange={e => this.handleReportsChange(e)}
                  disabled={this.state.submitInProgress}
                  readOnly={this.state.submitInProgress}
                />
                {' '}
                {REPORTS_LABEL }
              </label>
              <label htmlFor="other" className="lead-capture-form-label --checkbox-label">
                <input
                  name="other"
                  type="checkbox"
                  checked={this.state.other}
                  onChange={e => this.handleOtherChange(e)}
                  disabled={this.state.submitInProgress}
                  readOnly={this.state.submitInProgress}
                />
                {' '}
                {OTHER_LABEL}
              </label>
            </p>

            <p className="form-group lead-capture-form-group">
              <label htmlFor="message" className="lead-capture-form-label">
                Message to contributor
                <textarea
                  name="message"
                  value={this.state.message}
                  onChange={e => this.handleMessageChange(e)}
                  disabled={this.state.submitInProgress}
                  readOnly={this.state.submitInProgress}
                />
              </label>
            </p>

            <section className="form-group lead-capture-form-group --submit">
              <Loading variant="sip" ref={el => (this.loading = el)}>
                <button className="btn --dark" type="submit">Send Enquiry</button>
              </Loading>
            </section>
          </div>
        </div>
        <Footer />
      </form>
    );
  }
}
